import React from 'react';
import cn from 'classnames';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
  'aria-label'?: string;
}

/**
 * iOS-style toggle switch. Accessible (role="switch", keyboard operable)
 * and theme-aware; use it for on/off settings instead of a bare checkbox.
 */
export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'md',
  className,
  title,
  ...aria
}) => {
  const dims =
    size === 'sm'
      ? { track: 'h-4 w-7', thumb: 'h-3 w-3', travel: 'translate-x-3' }
      : { track: 'h-5 w-9', thumb: 'h-4 w-4', travel: 'translate-x-4' };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={aria['aria-label']}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
        dims.track,
        checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600',
        disabled && 'cursor-not-allowed opacity-40',
        className
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block transform rounded-full bg-white shadow ring-0 transition-transform duration-200',
          dims.thumb,
          checked ? dims.travel : 'translate-x-0.5'
        )}
      />
    </button>
  );
};
